import { Injectable } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { AiAuthenticationService } from '../services/aiAuthentication.service';

export interface TokenStorage {
  authToken: string;
  refreshToken: string;
  adminKey: string;
}

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  jwtHelperService = new JwtHelperService();
  private isRefreshing = false;
  private readonly subject: BehaviorSubject<TokenStorage> = new BehaviorSubject<TokenStorage>(null);
  private readonly aiSubject: BehaviorSubject<TokenStorage> = new BehaviorSubject<TokenStorage>(null);
  localstorageAuthorizationKey = 'last_known_authorization' as const;
  localstorageRefreshKey = 'refresh_token' as const;
  localstorageAIAuthorizationKey = 'ai_authorization' as const;
  localstorageAIRefreshKey = 'ai_refresh_token' as const;
  localstorageAdminKey = 'adminKey' as const;

  constructor(
    private readonly authService: AuthenticationService,
    private readonly aiAuthService: AiAuthenticationService,
    private readonly router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/Admin/')) {
      req = this.getRequestWithAdminKey(req);
      return next.handle(req); // no tap necessary
    }
    // no admin call

    // ai call
    if (req.url.includes('/ai/')) {
      const loadedAIAuthToken = window.localStorage.getItem(this.localstorageAIAuthorizationKey);
      try {
        if (!loadedAIAuthToken) {
          return this.handleAIRequest(req, next);
        }
        const decodedAIAuthToken = this.jwtHelperService.decodeToken(loadedAIAuthToken);
        if (!decodedAIAuthToken?.exp) {
          return this.handleAIRequest(req, next);
        }

        // is decode Token expired check
        if (DateTime.fromSeconds(decodedAIAuthToken.exp - 30) > DateTime.now()) {
          // token not expired
          req = this.setRequestHeader(req, 'authorization', loadedAIAuthToken);
          return this.handleAIRequest(req, next);
        }
        // decodedAuthToken.isExpired
        const loadedAIRefreshToken = window.localStorage.getItem(this.localstorageAIRefreshKey);
        if (!loadedAIRefreshToken) {
          req = this.setRequestHeader(req, 'authorization', loadedAIAuthToken);
          // req with token for unauthorized => redirect to login page
          return this.handleAIRequest(req, next);
        }
        const decodedAIRefreshToken = this.jwtHelperService.decodeToken(loadedAIRefreshToken);
        if (!decodedAIRefreshToken?.exp || DateTime.fromSeconds(decodedAIRefreshToken.exp) < DateTime.now()) {
          req = this.setRequestHeader(req, 'authorization', loadedAIAuthToken);
          // req with token for unauthorized => redirect to login page
          return this.handleAIRequest(req, next);
        }
        // refreshToken is not expired
        if (req.url.includes('Authorization/Refresh')) {
          req = this.setRequestHeader(req, 'authorization', loadedAIAuthToken);
          req = this.setRequestHeader(req, 'x-refresh-token', loadedAIRefreshToken);
          return this.handleAIRequest(req, next);
        }
        return this.refreshAIToken(req, next);
      } catch (error) {
        window.localStorage.removeItem(this.localstorageAIRefreshKey);
        window.localStorage.removeItem(this.localstorageAIAuthorizationKey);
        return this.handleAIRequest(req, next);
      }
    }

    // normal api call
    const loadedAuthToken = window.localStorage.getItem(this.localstorageAuthorizationKey);
    try {
      if (!loadedAuthToken) {
        return this.handleRequest(req, next);
      }
      const decodedAuthToken = this.jwtHelperService.decodeToken(loadedAuthToken);
      if (!decodedAuthToken?.exp) {
        return this.handleRequest(req, next);
      }

      // is decode Token expired check
      if (DateTime.fromSeconds(decodedAuthToken.exp - 30) > DateTime.now()) {
        // token not expired
        req = this.setRequestHeader(req, 'authorization', loadedAuthToken);
        return this.handleRequest(req, next);
      }
      // decodedAuthToken.isExpired

      if (this.router.url.includes('login')) {
        this.authService.logout();
        return this.handleRequest(req, next);
      }
      const loadedRefreshToken = window.localStorage.getItem(this.localstorageRefreshKey);
      if (!loadedRefreshToken) {
        req = this.setRequestHeader(req, 'authorization', loadedAuthToken);
        // req with token for unauthorized => redirect to login page
        return this.handleRequest(req, next);
      }
      const decodedRefreshToken = this.jwtHelperService.decodeToken(loadedRefreshToken);
      if (!decodedRefreshToken?.exp || DateTime.fromSeconds(decodedRefreshToken.exp) < DateTime.now()) {
        req = this.setRequestHeader(req, 'authorization', loadedAuthToken);
        // req with token for unauthorized => redirect to login page
        return this.handleRequest(req, next);
      }
      // refreshToken is not expired
      if (req.url.includes('Authorization/Refresh')) {
        req = this.setRequestHeader(req, 'authorization', loadedAuthToken);
        req = this.setRequestHeader(req, 'x-refresh-token', loadedRefreshToken);
        return this.handleRequest(req, next);
      }
      return this.refreshToken(req, next);
    } catch (error) {
      window.localStorage.removeItem(this.localstorageRefreshKey);
      window.localStorage.removeItem(this.localstorageAuthorizationKey);
      return this.handleRequest(req, next);
    }
  }

  /* old code
    req = this.getRequestWithTokens(req);
    return next.handle(req).pipe(
      tap(response => {
        if (response instanceof HttpResponse<Result>) {
          this.setTokensFromHeader(response.headers);
        }
      }),
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          this.setTokensFromHeader(error.headers);
          const parsedError = JSONbig.parse(error.error);
          if (error.status === 401 && parsedError.errorCode === this.ErrorCodes.EXPIRED_JWT_TOKEN) {
            return this.handle401Error(req, next);
          }
        }
        return throwError(() => error);
      })
    );

 */

  private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap(response => {
        if (response instanceof HttpResponse) {
          this.setTokensFromHeader(response.headers);
        }
      }),
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          this.setTokensFromHeader(error.headers);
        }
        return throwError(() => error);
      })
    );
  }

  private handleAIRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap(response => {
        if (response instanceof HttpResponse) {
          this.setAITokensFromHeader(response.headers);
        }
      }),
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          this.setAITokensFromHeader(error.headers);
        }
        return throwError(() => error);
      })
    );
  }

  private getRequestWithAdminKey(req: HttpRequest<any>) {
    let requestHeader = req.headers;
    const loadedAdminKey = window.localStorage.getItem(this.localstorageAdminKey);
    if (loadedAdminKey && loadedAdminKey !== 'null') {
      requestHeader = requestHeader.set('x-admin-key', loadedAdminKey);
    }
    req = req.clone({
      headers: requestHeader,
    });

    return req;
  }

  private refreshAIToken(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.aiSubject.next(null);
      return this.aiAuthService.refreshToken().pipe(
        switchMap(response => {
          this.isRefreshing = false;
          this.setAITokensFromHeader(response.headers);
          request = this.getRequestWithAITokens(request);
          return next.handle(this.addAITokenInHeader(request, this.transformHeaderToAITokenStorage(response.headers)));
        }),
        catchError(error => {
          this.isRefreshing = false;
          window.localStorage.removeItem(this.localstorageAIRefreshKey);
          window.localStorage.removeItem(this.localstorageAIAuthorizationKey);
          error.error = JSON.stringify(error.error);
          return throwError(() => error);
        })
      );
    } else {
      return this.aiSubject.pipe(
        filter((token): token is TokenStorage => token != null),
        // "(token): token is TokenStorage" tells TypeScript that after the filteroperation the value is guaranteed a TokenStorage
        take(1),
        switchMap((tokenStorage: TokenStorage) => {
          return next.handle(this.addAITokenInHeader(request, tokenStorage));
        })
      );
    }
  }

  getRequestWithAITokens(req: HttpRequest<any>): HttpRequest<any> {
    let requestHeader = req.headers;
    const loadedAuthToken = window.localStorage.getItem(this.localstorageAIAuthorizationKey);
    const loadedRefreshToken = window.localStorage.getItem(this.localstorageAIRefreshKey);
    if (loadedAuthToken && loadedAuthToken !== 'null') {
      requestHeader = requestHeader.set('authorization', loadedAuthToken);
    }
    if (loadedRefreshToken && loadedRefreshToken !== 'null') {
      requestHeader = requestHeader.set('x-refresh-token', loadedRefreshToken);
    }
    req = req.clone({
      headers: requestHeader,
    });

    return req;
  }

  setAITokensFromHeader(headers: HttpHeaders) {
    const retrievedAuthToken = headers.get('Authorization');
    if (retrievedAuthToken) {
      window.localStorage.setItem(this.localstorageAIAuthorizationKey, retrievedAuthToken);
    }
    const retrievedRefreshToken = headers.get('x-refresh-token');
    if (retrievedRefreshToken) {
      window.localStorage.setItem(this.localstorageAIRefreshKey, retrievedRefreshToken);
    }
    if (retrievedAuthToken || retrievedRefreshToken) {
      this.aiSubject.next(this.transformHeaderToAITokenStorage(headers));
    }
  }

  transformHeaderToAITokenStorage(headers: HttpHeaders): TokenStorage {
    return {
      authToken: headers.get('authorization') ?? '',
      refreshToken: headers.get('x-refresh-token') ?? '',
      adminKey: headers.get('x-admin-key') ?? '',
    };
  }

  addAITokenInHeader(request: HttpRequest<any>, tokenStorage: TokenStorage): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: tokenStorage.authToken,
        'X-Refresh-Token': tokenStorage.refreshToken,
        'X-Admin-Key': tokenStorage.adminKey,
      },
    });
  }

  private refreshToken(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.subject.next(null);
      return this.authService.refreshToken().pipe(
        switchMap(response => {
          this.isRefreshing = false;
          this.setTokensFromHeader(response.headers);
          request = this.getRequestWithTokens(request);
          return next.handle(this.addTokenInHeader(request, this.transformHeaderToTokenStorage(response.headers)));
        }),
        catchError(error => {
          this.isRefreshing = false;
          window.localStorage.removeItem(this.localstorageRefreshKey);
          window.localStorage.removeItem(this.localstorageAuthorizationKey);
          error.error = JSON.stringify(error.error);
          return throwError(() => error);
        })
      );
    } else {
      return this.subject.pipe(
        filter((token): token is TokenStorage => token != null),
        // "(token): token is TokenStorage" tells TypeScript that after the filteroperation the value is guaranteed a TokenStorage
        take(1),
        switchMap((tokenStorage: TokenStorage) => {
          return next.handle(this.addTokenInHeader(request, tokenStorage));
        })
      );
    }
  }

  getRequestWithTokens(req: HttpRequest<any>): HttpRequest<any> {
    let requestHeader = req.headers;
    const loadedAuthToken = window.localStorage.getItem(this.localstorageAuthorizationKey);
    const loadedRefreshToken = window.localStorage.getItem(this.localstorageRefreshKey);
    const loadedAdminKey = window.localStorage.getItem(this.localstorageAdminKey);
    if (loadedAuthToken && loadedAuthToken !== 'null') {
      requestHeader = requestHeader.set('authorization', loadedAuthToken);
    }
    if (loadedRefreshToken && loadedRefreshToken !== 'null') {
      requestHeader = requestHeader.set('x-refresh-token', loadedRefreshToken);
    }
    if (loadedAdminKey && loadedAdminKey !== 'null') {
      requestHeader = requestHeader.set('x-admin-key', loadedAdminKey);
    }
    req = req.clone({
      headers: requestHeader,
    });

    return req;
  }

  setTokensFromHeader(headers: HttpHeaders) {
    const retrievedAuthToken = headers.get('authorization');
    if (retrievedAuthToken) {
      window.localStorage.setItem(this.localstorageAuthorizationKey, retrievedAuthToken);
    }
    const retrievedRefreshToken = headers.get('x-refresh-token');
    if (retrievedRefreshToken) {
      window.localStorage.setItem(this.localstorageRefreshKey, retrievedRefreshToken);
    }
    if (retrievedAuthToken || retrievedRefreshToken) {
      this.subject.next(this.transformHeaderToTokenStorage(headers));
    }
  }

  transformHeaderToTokenStorage(headers: HttpHeaders): TokenStorage {
    return {
      authToken: headers.get('authorization') ?? '',
      refreshToken: headers.get('x-refresh-token') ?? '',
      adminKey: headers.get('x-admin-key') ?? '',
    };
  }

  addTokenInHeader(request: HttpRequest<any>, tokenStorage: TokenStorage): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: tokenStorage.authToken,
        'X-Refresh-Token': tokenStorage.refreshToken,
        'X-Admin-Key': tokenStorage.adminKey,
      },
    });
  }

  private setRequestHeader(req: HttpRequest<any>, headerName: string, headerValue: string) {
    if (!req) {
      throw new Error('Not Request Found');
    }
    if (!headerName) {
      throw new Error('Not Headername Provided');
    }
    let requestHeader = req.headers;
    if (headerValue && headerValue !== 'null' && headerValue !== 'undefined') {
      // null check and check for String null or String undefined
      requestHeader = requestHeader.set(headerName, headerValue);
    }
    req = req.clone({
      headers: requestHeader,
    });

    return req;
  }
}

export const httpInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }];
